import React, { useEffect, useState } from 'react';
import moment from 'moment';
import ActionsmonthView from './actionsmonth';
import Alert from '@mui/material/Alert';
import { useTranslation } from 'react-i18next';
import { monthNumberToText, formatCurrency } from '../common/common';
import { useActiveTenantCurrency } from '../common/currencyutils';
import { groupBy } from 'lodash';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import EventNoteIcon from '@mui/icons-material/EventNote';
import LeadStage, { buildLeadStageOptions } from '../domain/leadstage';

const Home = (props) => {
    const { t } = useTranslation();
    // Home is always scoped to the active tenant/portfolio (see doc/sweden-expansion-currency-plan.md).
    const currency = useActiveTenantCurrency();
    const [selectedLeadStage, setSelectedLeadStage] = useState('all');
    const { 
        getOverdueActionData, 
        getActionDataByMonthYear, 
        getOverdueLeadTasks,
        getUpcomingLeadTasks,
        getCustomerDescriptiveInfo,
        authenticated, 
        userProfile, 
        customerDescriptionsOwn,
        customerDescriptionsOwnFetching,
    } = props;

    useEffect(() => {
        let start = moment();
        if (authenticated && userProfile && userProfile.isSalesUser) {
            getActionDataByMonthYear(start.format('M'), start.format('YYYY'), 1);
            getOverdueActionData();
            getOverdueLeadTasks();
            getUpcomingLeadTasks();
            if (!customerDescriptionsOwn && !customerDescriptionsOwnFetching) {
                getCustomerDescriptiveInfo();
            }
        }
    }, [
        authenticated,
        userProfile,
        getActionDataByMonthYear,
        getOverdueActionData,
        getOverdueLeadTasks,
        getUpcomingLeadTasks,
        getCustomerDescriptiveInfo,
        customerDescriptionsOwn,
        customerDescriptionsOwnFetching,
    ]);

    const getCurrentMonthData = (actions) => {
        let result = [];
        for (let act of actions) {
            let dueDate = moment(act.due);
            let minDate = moment().date(1).hour(0).minute(0);
            let maxDate = moment().date(1).hour(0).minute(0).add(1, 'M');
            if (dueDate.isSameOrAfter(minDate) && dueDate.isBefore(maxDate)) {
                result.push(act);
            }
        }
        return result;
    };

    const getNextMonthData = (actions) => {
        let result = [];
        for (let act of actions) {
            let dueDate = moment(act.due);
            let minDate = moment().date(1).hour(0).minute(0).add(1, 'M');
            let maxDate = moment().date(1).hour(0).minute(0).add(2, 'M');
            if (dueDate.isSameOrAfter(minDate) && dueDate.isBefore(maxDate)) {
                result.push(act);
            }
        }
        return result;
    };

    const getSales = (actions) => {
        let total = 0;
        let deals = 0;
        for (let action of actions) {
            total += parseFloat(action.locationOrderValue);
            if (action.completed != null) {
                deals += parseFloat(action.locationOrderValue);
            }
        }
        return { potential: total, actual: deals };
    };

    const getByCustomerList = (actions) => {
        let customers = [];
        let orphans = [];
        for (let act of actions) {
            if (act.completed != null) continue;
            if (act.customer != null) {
                let cus = customers.find((c) => c.id === act.customer.id);
                if (cus != null) {
                    cus.locationCount++;
                    cus.actionList.push(act);
                } else {
                    //create new customer to list
                    cus = act.customer;
                    cus.locationCount = 1;
                    cus.actionList = [];
                    cus.actionList.push(act);
                    customers.push(cus);
                }
            } else {
                orphans.push(act);
            }
        }
        customers.sort((a, b) => parseInt(a.locationCount, 10) - parseInt(b.locationCount, 10)).reverse();
        return { withcustomer: customers, nocustomer: orphans };
    };

    const getMonthName = () => {
        return monthNumberToText(moment().month() + 1);
    };

    const getNextMonthName = () => {
        return monthNumberToText(moment().add(1, 'month').month() + 1);
    };

    // key : 2022-01 --> '2023 Tammikuu
    const getOverdueMonthNameFromKey = (key) => {
        const year = key.substring(0, 4);
        const month = monthNumberToText(parseInt(key.substring(5)));
        return year + ' ' + month;
    };

    const getDaysOverdue = (dueDate) => {
        const due = moment(dueDate);
        const now = moment();
        const days = now.diff(due, 'days');
        return days > 0 ? days : 0;
    };

    const getLeadSummary = (customers, stageOptions) => {
        const summary = {
            total: 0,
            open: 0,
            won: 0,
            lost: 0,
            dealValue: 0,
            byStage: {},
        };
        for (const option of stageOptions) summary.byStage[option.value] = 0;

        for (const customer of customers || []) {
            if (customer == null || customer.leadStage == null) continue;
            summary.total++;
            const stage = customer.leadStage;
            summary.byStage[stage] = (summary.byStage[stage] || 0) + 1;
            if (stage === LeadStage.Won()) {
                summary.won++;
                summary.dealValue += parseFloat(customer.leadWonValue || 0);
            } else if (stage === LeadStage.Lost()) {
                summary.lost++;
            } else {
                summary.open++;
            }
        }

        return summary;
    };

    const getFilteredLeadCustomers = (customers, stage) => {
        const results = [];
        for (const customer of customers || []) {
            if (customer == null || customer.leadStage == null) continue;
            if (stage !== 'all' && customer.leadStage !== stage) continue;
            results.push(customer);
        }
        return results.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    };

    const leadStageOptions = buildLeadStageOptions(props.configurableLeadStages, t);
    const leadSummary = getLeadSummary(customerDescriptionsOwn, leadStageOptions);
    const leadStageLabels = leadStageOptions.map((option) => [option.value, option.label]);
    const leadStageFilters = [['all', t('general.all')], ...leadStageLabels];
    const filteredLeadCustomers = getFilteredLeadCustomers(customerDescriptionsOwn, selectedLeadStage);
    const selectedStageCount = selectedLeadStage === 'all' ? leadSummary.total : leadSummary.byStage[selectedLeadStage] || 0;
    const closedLeadCount = leadSummary.won + leadSummary.lost;
    const conversionRate = closedLeadCount > 0 ? Math.round((leadSummary.won / closedLeadCount) * 100) : 0;
    const actions = props.actions || [];
    const overdueActions = props.overdueActions || [];
    const hasReminderData = actions.length > 0 || overdueActions.length > 0;
    const hasLeadData = leadSummary.total > 0 || (props.overdueLeadTasks && props.overdueLeadTasks.length > 0) || (props.upcomingLeadTasks && props.upcomingLeadTasks.length > 0);

    if (props.fetching === true) return null;
    if (!props.userProfile.isSalesUser) {
        if (props.userProfile.isProductionUser) {
            return (
                <div style={{ padding: '20px' }}>
                    <Alert severity="info">Tuotanto</Alert>
                </div>
            );
        } else if (props.userProfile.isInvoicingUser) {
            return (
                <div style={{ padding: '20px' }}>
                    <Alert severity="info">Laskutus</Alert>
                </div>
            );
        }
    }
    if (!hasReminderData && !hasLeadData) {
        return (
            <div style={{ padding: '20px' }}>
                <Alert severity="info">{t('frontpage.noReminders')}</Alert>
            </div>
        );
    } else {
        const current = getCurrentMonthData(actions);
        const sales = getSales(current);
        const cusByLocCount = getByCustomerList(current);

        const next = getNextMonthData(actions);
        const nextSales = getSales(next);
        const nextCusByLocCount = getByCustomerList(next);

        const overdueGrouped =
            overdueActions.length > 0
                ? groupBy(overdueActions, (a) => moment(a.due).format('YYYY-MM'))
                : {};

        return (
            <Grid container spacing={2}>
                {hasLeadData && (
                    <Grid item xs={12} md={5}>
                        <Accordion defaultExpanded sx={{ mb: 2 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Grid container alignItems="center" spacing={1}>
                                    <Grid item xs={12} sm="auto">
                                        <Typography variant="h6">{t('frontpage.leadSectionTitle')}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {props.overdueLeadTasks && props.overdueLeadTasks.length > 0 && (
                                            <Chip
                                                size="small"
                                                color="error"
                                                icon={<WarningAmberIcon />}
                                                label={`${t('frontpage.overdueLeadTasks')}: ${props.overdueLeadTasks.length}`}
                                            />
                                        )}
                                        {props.upcomingLeadTasks && props.upcomingLeadTasks.length > 0 && (
                                            <Chip
                                                size="small"
                                                color="warning"
                                                icon={<EventNoteIcon />}
                                                label={`${t('frontpage.upcomingLeadTasks')}: ${props.upcomingLeadTasks.length}`}
                                            />
                                        )}
                                        {leadSummary.total > 0 && (
                                            <Chip
                                                size="small"
                                                label={`${t('frontpage.leadSummaryOpen')}: ${leadSummary.open}`}
                                            />
                                        )}
                                        {leadSummary.total > 0 && (
                                            <Chip
                                                size="small"
                                                color="success"
                                                label={`${t('frontpage.leadSummaryWon')}: ${leadSummary.won}`}
                                            />
                                        )}
                                    </Grid>
                                </Grid>
                            </AccordionSummary>
                            <AccordionDetails sx={{ display: 'block' }}>
                    {props.overdueLeadTasks && props.overdueLeadTasks.length > 0 && (
                        <Card sx={{ mb: 2, borderLeft: 4, borderColor: 'error.main' }}>
                            <CardContent>
                                <Typography
                                    variant="h6"
                                    sx={{ color: 'error.main', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <WarningAmberIcon fontSize="small" />
                                    {t('frontpage.overdueLeadTasks')} ({props.overdueLeadTasks.length})
                                </Typography>
                                <List sx={{ pt: 0 }}>
                                    {props.overdueLeadTasks.map((task) => (
                                        <ListItem
                                            key={task.storageId}
                                            onClick={() => props.history.push(`/customers/${task.customerId}`)}
                                            sx={{
                                                cursor: 'pointer',
                                                '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' },
                                                py: 1,
                                            }}>
                                            <ListItemText
                                                primary={task.description}
                                                secondary={
                                                    <span>
                                                        {task.customerName} • {t('invoice.dueDate')}: {moment(task.dueAt).format('DD.MM.YYYY HH:mm')}
                                                    </span>
                                                }
                                            />
                                            <Chip
                                                label={`${getDaysOverdue(task.dueAt)} ${t('invoice.daysOverdue')}`}
                                                color="error"
                                                size="small"
                                                sx={{ ml: 1 }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </CardContent>
                        </Card>
                    )}
                    {props.upcomingLeadTasks && props.upcomingLeadTasks.length > 0 && (
                        <Card sx={{ mb: 2, borderLeft: 4, borderColor: 'warning.main' }}>
                            <CardContent>
                                <Typography
                                    variant="h6"
                                    sx={{ color: '#8a6100', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <EventNoteIcon fontSize="small" />
                                    {t('frontpage.upcomingLeadTasks')} ({props.upcomingLeadTasks.length})
                                </Typography>
                                <List sx={{ pt: 0 }}>
                                    {props.upcomingLeadTasks.map((task) => (
                                        <ListItem
                                            key={task.storageId}
                                            onClick={() => props.history.push(`/customers/${task.customerId}`)}
                                            sx={{
                                                cursor: 'pointer',
                                                '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' },
                                                py: 1,
                                            }}>
                                            <ListItemText
                                                primary={task.description}
                                                secondary={
                                                    <span>
                                                        {task.customerName} • {t('invoice.dueDate')}: {moment(task.dueAt).format('DD.MM.YYYY HH:mm')}
                                                    </span>
                                                }
                                            />
                                            <Chip
                                                label={moment(task.dueAt).format('HH:mm')}
                                                color="warning"
                                                size="small"
                                                sx={{ ml: 1 }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </CardContent>
                        </Card>
                    )}
                    {leadSummary.total > 0 && (
                        <Card sx={{ mb: 2 }}>
                            <CardContent>
                                <Typography variant="h6" sx={{ mb: 1 }}>
                                    {t('frontpage.leadSummaryTitle')}
                                </Typography>
                                <Grid container spacing={1} sx={{ mb: 1 }}>
                                    <Grid item xs={6} sm={3}>
                                        <Chip label={`${t('frontpage.leadSummaryTotal')}: ${leadSummary.total}`} />
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <Chip label={`${t('frontpage.leadSummaryOpen')}: ${leadSummary.open}`} />
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <Chip color="success" label={`${t('frontpage.leadSummaryWon')}: ${leadSummary.won}`} />
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <Chip color="error" label={`${t('frontpage.leadSummaryLost')}: ${leadSummary.lost}`} />
                                    </Grid>
                                </Grid>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {t('frontpage.leadSummaryDealValue')}: {formatCurrency(leadSummary.dealValue, 2, currency)}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {t('frontpage.leadSummaryConversion')}: {conversionRate}%
                                </Typography>
                                <Divider sx={{ my: 1 }} />
                                <Grid container spacing={1}>
                                    {leadStageFilters.map(([stage, label]) => (
                                        <Grid item key={stage}>
                                            <Chip
                                                data-testid={`lead-stage-filter-${stage}`}
                                                label={`${label}: ${stage === 'all' ? leadSummary.total : leadSummary.byStage[stage] || 0}`}
                                                size="small"
                                                color={selectedLeadStage === stage ? 'primary' : 'default'}
                                                variant={selectedLeadStage === stage ? 'filled' : 'outlined'}
                                                onClick={() => setSelectedLeadStage(stage)}
                                                sx={{ cursor: 'pointer' }}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                                <Divider sx={{ my: 1 }} />
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    {t('frontpage.leadSummaryCustomers')} ({selectedStageCount})
                                </Typography>
                                {filteredLeadCustomers.length === 0 && (
                                    <Typography variant="body2">{t('frontpage.leadSummaryNoCustomersInStage')}</Typography>
                                )}
                                {filteredLeadCustomers.length > 0 && (
                                    <List sx={{ pt: 0 }}>
                                        {filteredLeadCustomers.map((customer) => (
                                            <ListItem
                                                key={customer.id}
                                                data-testid={`lead-stage-customer-${customer.id}`}
                                                onClick={() => props.history.push(`/customers/${customer.id}`)}
                                                sx={{
                                                    cursor: 'pointer',
                                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' },
                                                    py: 1,
                                                }}>
                                                <ListItemText
                                                    primary={customer.name}
                                                    secondary={customer.businessId ? customer.businessId : ''}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                )}
                            </CardContent>
                        </Card>
                    )}
                            </AccordionDetails>
                        </Accordion>
                    </Grid>
                )}
                <Grid item xs={12} md={hasLeadData ? 7 : 12}>
                    {Object.keys(overdueGrouped).length > 0 &&
                        Object.keys(overdueGrouped).map((om) => (
                            <ActionsmonthView
                                key={'expired-'+getOverdueMonthNameFromKey(om)}
                                monthname={t('frontpage.expiredReminders') + ', ' + getOverdueMonthNameFromKey(om)}
                                actions={overdueGrouped[om]}
                                sales={getSales(overdueGrouped[om])}
                                cusByLocCount={getByCustomerList(overdueGrouped[om])}
                                expanded={false}
                                newOrderFromLocations={props.newOrderFromLocations}
                                warning={true}
                                showProgress={false}
                                history={props.history}
                                currency={currency}
                            />
                        ))}
                    <ActionsmonthView
                        key={'current-month-'+getMonthName()}
                        monthname={getMonthName()}
                        actions={current}
                        sales={sales}
                        cusByLocCount={cusByLocCount}
                        pendingOrders={props.pendingOrders}
                        newOrderFromLocations={props.newOrderFromLocations}
                        expanded={true}
                        showProgress={true}
                        history={props.history}
                        currency={currency}
                    />

                    <ActionsmonthView
                        key={'next-month-'+getNextMonthName()}
                        monthname={getNextMonthName()}
                        actions={next}
                        sales={nextSales}
                        cusByLocCount={nextCusByLocCount}
                        pendingOrders={props.pendingOrders}
                        newOrderFromLocations={props.newOrderFromLocations}
                        expanded={false}
                        showProgress={true}
                        history={props.history}
                        currency={currency}
                    />
                </Grid>
            </Grid>
        );
    }
};

export default Home;
